import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Primitives";
import { BEFORE_AFTER } from "@/data/content";

const N = BEFORE_AFTER.length;
const EASE = [0.22, 1, 0.36, 1];

const variants = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0.4 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0.4 }),
};

export const BeforeAfter = () => {
  const [[index, dir], setState] = useState([0, 0]);
  const [paused, setPaused] = useState(false);

  const paginate = useCallback((d) => {
    setState(([i]) => [(i + d + N) % N, d]);
  }, []);

  const goTo = (i) => setState(([cur]) => [i, i > cur ? 1 : -1]);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => paginate(1), 5500);
    return () => clearInterval(t);
  }, [paused, paginate]);

  const item = BEFORE_AFTER[index];

  return (
    <section id="results" className="bg-[#050505] py-16 md:py-24">
      <div className="mx-auto max-w-[1100px] px-5 md:px-10">
        <Reveal>
          <SectionHeading
            overline="Real Results"
            title={<>The Perfect Touch <span className="gold-gradient italic">difference</span></>}
            subtitle="Real homes and spaces we've transformed across New York — slide through genuine before-and-after transformations and spotless finished results."
          />
        </Reveal>

        <Reveal delay={0.1}>
          <div
            className="mt-12 relative"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="before-after-carousel"
          >
            <div className="relative overflow-hidden border border-white/10 bg-black aspect-[3/2]">
              <AnimatePresence initial={false} custom={dir} mode="popLayout">
                <motion.img
                  key={index}
                  src={item.image}
                  alt={`${item.label} — cleaning result by Perfect Touch`}
                  custom={dir}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.6, ease: EASE }}
                  className="absolute inset-0 w-full h-full object-contain"
                  draggable={false}
                />
              </AnimatePresence>

              {/* Caption overlay */}
              <div className="absolute bottom-0 left-0 right-0 z-10 flex items-center justify-between gap-3 px-5 py-4 bg-gradient-to-t from-black/85 to-transparent pointer-events-none">
                <span className="font-display text-base md:text-xl text-white">{item.label}</span>
                <span className="text-[10px] md:text-xs uppercase tracking-[0.16em] text-gold whitespace-nowrap">{item.area}</span>
              </div>

              {/* Arrows */}
              <button
                data-testid="ba-prev"
                onClick={() => paginate(-1)}
                aria-label="Previous transformation"
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/50 backdrop-blur-sm border border-white/15 text-white hover:bg-gold hover:text-black hover:border-gold transition-colors duration-300"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="ba-next"
                onClick={() => paginate(1)}
                aria-label="Next transformation"
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-black/50 backdrop-blur-sm border border-white/15 text-white hover:bg-gold hover:text-black hover:border-gold transition-colors duration-300"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* Dots */}
            <div className="mt-5 flex items-center justify-center gap-2.5">
              {BEFORE_AFTER.map((_, i) => (
                <button
                  key={i}
                  data-testid={`ba-dot-${i}`}
                  onClick={() => goTo(i)}
                  aria-label={`Go to transformation ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === index ? "w-8 bg-gold" : "w-2.5 bg-white/25 hover:bg-white/50"
                  }`}
                />
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
